import { useState, useMemo, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "../ui/table";
import { Dropdown } from "../ui/dropdown/Dropdown";
import { DropdownItem } from "../ui/dropdown/DropdownItem";
import ReservationsTab from "../common/ReservationsTab";
import { ChevronDownIcon, CloseIcon, ChevronLeftIcon } from "../../icons";
import { StarIcon as StarIconOutline } from "@heroicons/react/24/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid";
import { ReservationRequest, RequestStatus } from "../../types/request";
import { LoadingSpinner } from "../ui/LoadingSpinner";
import { PageLoadingView } from "../common/PageLoadingView";
import { PageErrorView } from "../common/PageErrorView";
import { FrameType } from "../../types/request";
import Alert from "../ui/alert/Alert";
import SimpleDate from "../common/SimpleDate";
import { useReservationRequests } from "../../hooks/useReservationRequests";
import { useMessageTypes } from "../../hooks/useMessageTypes";
import { useUser } from "../../context/UserInfoProvider";
import { useAnalytics } from "../../hooks/useAnalytics";
import { useHotelInfo } from "../../hooks/useHotelInfo";
import { useAuthenticatedApi } from "../../hooks/useAuthenticatedApi";
import { useRequestRefresh } from "../../hooks/useRequestRefresh";
import ConfirmationDialog from "../common/ConfirmationDialog";
import MessageHistorySidebar from "../common/MessageHistorySidebar";
import { RequestsToolbar } from "../common/RequestsToolbar";

// Helper to display nullable fields
const displayValue = (
  value: string | number | null | undefined,
  defaultValue: string = "N/A",
) => {
  return value !== null && value !== undefined && String(value).trim() !== ""
    ? String(value)
    : defaultValue;
};

// Helper to format date with timezone support
const formatDateWithTimezone = (
  dateString: string,
  timezone?: string | null,
) => {
  const date = new Date(dateString);

  if (timezone) {
    // Use the hotel's timezone
    return date.toLocaleString("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } else {
    // Fallback to local timezone
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }
};

// Define the TypeScript interface for the table rows
export interface ReservationRequestData {
  id: string;
  facilityType: string;
  facilityId: string;
  reservationTime: string;
  partySize: number;
  specialRequests: string;
  duration: number;
  status: RequestStatus;
}

export default function ReservationReqs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { callApi } = useAuthenticatedApi();
  const { platformUserId } = useUser();
  const analytics = useAnalytics();
  const { data: hotelInfo } = useHotelInfo();
  const [selectedFrame, setSelectedFrame] = useState<FrameType>("New");
  const [selectedRequest, setSelectedRequest] =
    useState<ReservationRequest | null>(null);
  const [starredList, setStarredList] = useState<Set<string>>(new Set());
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isSidebarStatusDropdownOpen, setIsSidebarStatusDropdownOpen] =
    useState(false);
  const [sortBy, setSortBy] = useState<"time" | "requestId">("time");
  const [liveStatus, setLiveStatus] = useState("");
  const [isPushingStatus, setIsPushingStatus] = useState(false);
  const [subject, setSubject] = useState("");
  const [showPushAlert, setShowPushAlert] = useState(false);
  const [showStatusUpdateAlert, setShowStatusUpdateAlert] = useState(false);
  const [showStarredOnly, setShowStarredOnly] = useState(false);

  // Track which status dropdowns are open
  const [statusDropdowns, setStatusDropdowns] = useState<{
    [key: string]: boolean;
  }>({});

  // Track which items are being updated
  const [updatingStatus, setUpdatingStatus] = useState<{
    [key: string]: boolean;
  }>({});

  // Refs for status dropdown buttons
  const statusButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>(
    {},
  );

  // Local search term state for UI
  const [searchTermLocal, setSearchTermLocal] = useState<string>("");

  // Determine status filter based on selected frame
  const statusFilter = useMemo(() => {
    if (selectedFrame === "New") {
      return [
        "Submitted",
        "InProgress",
        "Delayed",
      ] as unknown as RequestStatus[];
    } else if (selectedFrame === "Scheduled") {
      return ["Scheduled"] as unknown as RequestStatus[];
    } else if (selectedFrame === "History") {
      return ["Completed", "Cancelled"] as unknown as RequestStatus[];
    }
    return undefined;
  }, [selectedFrame]);

  // Use the reservation requests hook
  const {
    requests: fetchedRequests,
    isLoading,
    error,
    updateRequestStatus,
    getReservationRequest,
    pagination,
    setPage,
    setLimit,
    setSearchTerm,
    refetch,
  } = useReservationRequests({
    hotelId: hotelInfo?.hotelid || "",
    initialPage: 1,
    initialLimit: 10,
    initialStatus: statusFilter,
    sortBy: sortBy === "time" ? "reservationTime" : "requestId",
    sortOrder: "desc",
  });

  // Setup auto-refresh when navigating to the page and when new requests come in
  useRequestRefresh({
    onRefresh: () => {
      refetch();
    },
    requestType: "reservation",
  });

  // Persist favorites locally (scoped by user and page)
  const favoritesStorageKey = useMemo(() => {
    const uid = platformUserId || "anon";
    return `selfserve:favorites:reservation:${uid}`;
  }, [platformUserId]);

  const [hasLoadedFavorites, setHasLoadedFavorites] = useState(false);

  useEffect(() => {
    setHasLoadedFavorites(false);
  }, [favoritesStorageKey]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(favoritesStorageKey);
      if (raw) {
        const ids: string[] = JSON.parse(raw);
        if (Array.isArray(ids)) setStarredList(new Set(ids));
      } else {
        setStarredList(new Set());
      }
    } catch {
      setStarredList(new Set());
    }
    setHasLoadedFavorites(true);
  }, [favoritesStorageKey]);

  useEffect(() => {
    if (!hasLoadedFavorites) return;
    try {
      localStorage.setItem(
        favoritesStorageKey,
        JSON.stringify(Array.from(starredList)),
      );
    } catch {
      // no-op
    }
  }, [starredList, favoritesStorageKey, hasLoadedFavorites]);

  // State for message types dropdown
  // Message types state
  const { messageTypes, isLoadingMessageTypes, messageTypeError } =
    useMessageTypes();
  const [selectedMessageTypeId, setSelectedMessageTypeId] =
    useState<string>("");

  // Message confirmation and history states
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<{
    subject: string;
    content: string;
    typeId: string;
  } | null>(null);
  const [showMessageHistory, setShowMessageHistory] = useState(false);
  // Auto-open from URL deep link if requestId is present (guard to run once)
  const [didInitDeepLink, setDidInitDeepLink] = useState(false);

  // Track navigation state
  const [pendingNavigation, setPendingNavigation] = useState<
    "previous" | "next" | null
  >(null);

  // Statuses should be TitleCase to match backend enum expectations
  const statusOptions: RequestStatus[] = [
    "Submitted",
    "Completed",
  ] as unknown as RequestStatus[];

  // Replace the old hook with our authenticated API implementation

  // Remove frontend filtering - backend handles search and status filtering
  const filteredData = useMemo(() => {
    if (!fetchedRequests?.data) return [];
    return fetchedRequests.data.filter((request) => {
      // Only filter by starred status - backend handles status filtering
      const matchesStarred = showStarredOnly
        ? starredList.has(request.requestId)
        : true;
      return matchesStarred;
    });
  }, [fetchedRequests?.data, showStarredOnly, starredList]);

  // Sort the filtered data
  const sortedData = useMemo(() => {
    // Sorting handled by backend; keep current page order
    return filteredData;
  }, [filteredData]);

  // No client-side pagination needed - backend handles pagination
  const paginatedData = sortedData;

  const totalPages = Math.ceil(pagination.totalCount / pagination.limit);
  const hasMore = pagination.page < totalPages;

  // Reset pagination when switching tabs
  useEffect(() => {
    setPage(1);
  }, [selectedFrame, setPage]);

  // Initialize selected tab from statusTab param
  useEffect(() => {
    const statusTab = new URLSearchParams(window.location.search).get(
      "statusTab",
    );
    if (statusTab && (statusTab === "New" || statusTab === "Completed")) {
      setSelectedFrame(statusTab as FrameType);
    }
  }, []);

  useEffect(() => {
    if (didInitDeepLink) return;
    const reqId = searchParams.get("requestId");
    if (!reqId) return;

    // If already selected, mark as done
    if (selectedRequest && selectedRequest.requestId === reqId) {
      setDidInitDeepLink(true);
      return;
    }

    // Check if the request exists in the current page's data
    const match =
      filteredData.find((r) => r.requestId === reqId) ||
      sortedData.find((r) => r.requestId === reqId);

    if (match) {
      // Request found in current page - select it
      handleSelectRequest(match);
      setDidInitDeepLink(true);
    } else if (!isLoading) {
      // Request not in current page - fetch it directly by ID
      const fetchAndSelectRequest = async () => {
        try {
          const completeRequest = await getReservationRequest(reqId);
          setSelectedRequest(completeRequest);
          setDidInitDeepLink(true);
        } catch (error) {
          console.error("Failed to fetch deep-linked request:", error);
          setDidInitDeepLink(true); // Mark as done even on error to prevent infinite loop
        }
      };
      fetchAndSelectRequest();
    }
  }, [
    searchParams,
    selectedRequest,
    filteredData,
    sortedData,
    didInitDeepLink,
    isLoading,
    getReservationRequest,
  ]);

  // Auto-select first message type when available
  useEffect(() => {
    if (messageTypes.length > 0 && !selectedMessageTypeId) {
      setSelectedMessageTypeId(messageTypes[0].typeid);
    }
  }, [messageTypes, selectedMessageTypeId]);

  // Handle navigation across pages - clear sidebar when navigating to new page
  useEffect(() => {
    if (pendingNavigation) {
      setSelectedRequest(null);
    }
  }, [pendingNavigation]);

  // Handle navigation across pages - select appropriate item after page loads
  useEffect(() => {
    if (pendingNavigation && sortedData.length > 0) {
      // Small delay to ensure data is loaded
      const timer = setTimeout(() => {
        if (pendingNavigation === "previous") {
          // Select the last item of the new page
          const lastItem = sortedData[sortedData.length - 1];
          if (lastItem) {
            handleSelectRequest(lastItem);
          }
        } else if (pendingNavigation === "next") {
          // Select the first item of the new page
          const firstItem = sortedData[0];
          if (firstItem) {
            handleSelectRequest(firstItem);
          }
        }
        setPendingNavigation(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [pendingNavigation, sortedData]);

  if (isLoading) {
    return <PageLoadingView />;
  }

  if (error) {
    return (
      <PageErrorView
        title="Error loading reservation requests"
        onRetry={refetch}
      />
    );
  }

  const handleSelectRequest = async (req: ReservationRequest) => {
    try {
      const completeRequest = await getReservationRequest(req.requestId);
      setSelectedRequest(completeRequest);
      setStatusError(null);
      // Sync URL for deep linking (do not auto-open message history)
      try {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set("requestId", req.requestId);
          return next;
        });
      } catch {}
    } catch (err) {
      const error = err as Error;
      setStatusError(error.message || "Failed to load request details");
    }
  };

  const handleSidebarStatusChange = async (newStatus: RequestStatus) => {
    if (!selectedRequest || isUpdatingStatus) return;

    setIsUpdatingStatus(true);
    setStatusError(null);
    setIsSidebarStatusDropdownOpen(false);

    try {
      // newStatus will be TitleCase from statusOptions
      const apiCompatibleStatus = newStatus; // Should already be "Submitted" or "Completed"

      const updatePayload: {
        requestId: string;
        status: RequestStatus;
        completedAt?: string;
      } = {
        requestId: selectedRequest.requestId,
        status: apiCompatibleStatus, // This is now TitleCase
      };

      if ((apiCompatibleStatus as string) === "Completed") {
        updatePayload.completedAt = new Date().toISOString();
      }

      await updateRequestStatus(updatePayload);

      // Update local state
      setSelectedRequest((prev) =>
        prev
          ? {
              ...prev,
              status: apiCompatibleStatus, // Update with TitleCase
              completedAt:
                (apiCompatibleStatus as string) === "Completed"
                  ? updatePayload.completedAt
                  : prev.completedAt,
            }
          : null,
      );

      // Track reservation request status change analytics
      analytics.request.statusChange(
        "reservation",
        apiCompatibleStatus.toLowerCase(),
        selectedRequest.status?.toLowerCase(),
      );

      // Wait for refetch to complete before showing toast
      await refetch();
      setShowStatusUpdateAlert(true);
      setTimeout(() => setShowStatusUpdateAlert(false), 3000);

      // Don't change the selected frame (tab) when updating status from sidebar
      // User should remain on their current tab
    } catch (error) {
      setStatusError("Failed to update status. Please try again.");
      console.error("Error updating status:", error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const toggleStars = (requestId: string) => {
    setStarredList((prev) => {
      const updatedSet = new Set(prev);
      if (updatedSet.has(requestId)) {
        updatedSet.delete(requestId);
      } else {
        updatedSet.add(requestId);
      }
      return updatedSet;
    });
  };

  const toggleSortDropdown = () => setIsSortDropdownOpen(!isSortDropdownOpen);

  const toggleStatusDropdown = (requestId: string) => {
    setStatusDropdowns((prev) => {
      const newState = { ...prev };
      Object.keys(newState).forEach((key) => {
        if (key !== requestId) {
          newState[key] = false;
        }
      });
      newState[requestId] = !newState[requestId];
      return newState;
    });
  };

  const handleTableStatusChange = async (
    requestId: string,
    newStatus: string,
  ) => {
    try {
      setUpdatingStatus((prev) => ({ ...prev, [requestId]: true }));
      setStatusDropdowns((prev) => ({ ...prev, [requestId]: false }));

      // newStatus will be TitleCase from statusOptions
      const apiCompatibleStatus = newStatus; // Should already be "Submitted" or "Completed"

      const updatePayload: {
        requestId: string;
        status: RequestStatus;
        completedAt?: string;
      } = {
        requestId,
        status: apiCompatibleStatus as RequestStatus,
      };

      if ((apiCompatibleStatus as string) === "Completed") {
        updatePayload.completedAt = new Date().toISOString();
      }

      await updateRequestStatus(updatePayload);

      // Track reservation request status change analytics
      analytics.request.statusChange(
        "reservation",
        apiCompatibleStatus.toLowerCase(),
        "",
      );

      // Wait for refetch to complete before showing toast
      await refetch();
      setShowStatusUpdateAlert(true);
      setTimeout(() => setShowStatusUpdateAlert(false), 3000);

      // Don't change the selected frame (tab) when updating status from table
      // User should remain on their current tab
    } catch (error) {
      setStatusError("Failed to update status. Please try again.");
      console.error("Error updating status:", error);
    } finally {
      setUpdatingStatus((prev) => ({ ...prev, [requestId]: false }));
      setStatusDropdowns((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  const getFrameClass = () => "text-[#1100D8] font-[300] dark:text-gray-400";

  const getRowClassName = (status: string) => {
    if (status === "Completed") {
      return "bg-gray-50 dark:bg-transparent";
    }
    return "";
  };

  const getStatusDisplay = (status: RequestStatus) => {
    // Handle potential TitleCase from backend/statusOptions
    if ((status as string) === "Submitted") return "Submitted";
    if ((status as string) === "Completed") return "Completed";
    // Fallback for any other unexpected values, though statusOptions is limited
    return (
      String(status).charAt(0).toUpperCase() +
      String(status).slice(1).toLowerCase()
    );
  };

  const handlePushToGuest = async () => {
    if (!liveStatus.trim()) {
      setStatusError("Message content is required");
      return;
    }
    if (!selectedMessageTypeId) {
      setStatusError("Please select a message type.");
      return;
    }

    if (!selectedRequest) {
      setStatusError("No request selected");
      return;
    }
    if (!platformUserId) {
      setStatusError(
        "Sender User ID (platformUserId) is missing or not configured.",
      );
      return;
    }
    // Use platformUserId from selectedRequest for the receiver
    if (!selectedRequest.platformUserId) {
      setStatusError(
        "Receiver (guest) platform user ID (platformUserId) is missing for this request.",
      );
      console.error(
        "Receiver platformUserId missing on selectedRequest:",
        selectedRequest,
      );
      return;
    }

    // Show confirmation dialog instead of sending immediately
    setPendingMessage({
      subject: subject || "",
      content: liveStatus,
      typeId: selectedMessageTypeId,
    });
    setShowConfirmDialog(true);
  };

  const handleConfirmSend = async () => {
    if (!pendingMessage || !selectedRequest) return;

    setIsPushingStatus(true);
    setStatusError(null);
    setShowConfirmDialog(false);

    try {
      await callApi({
        method: "POST",
        url: "/management/messages",
        data: {
          senderPlatformUserId: platformUserId,
          receiverPlatformUserId: selectedRequest.platformUserId,
          typeId: pendingMessage.typeId,
          subject: pendingMessage.subject,
          content: pendingMessage.content,
          requestId: selectedRequest.requestId,
        },
      });

      // Clear fields immediately after successful send
      setLiveStatus("");
      setSubject("");
      setPendingMessage(null);

      // Then show success alert
      setShowPushAlert(true);
      setTimeout(() => setShowPushAlert(false), 3000);
    } catch (err) {
      const error = err as Error;
      setStatusError(error.message || "Failed to send message to guest");
      console.error("Error sending message:", error);
    } finally {
      setIsPushingStatus(false);
    }
  };

  const handleCancelSend = () => {
    setShowConfirmDialog(false);
    setPendingMessage(null);
  };

  return (
    <div className="flex flex-col h-full p-6">
      {/* Header + Subheader */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-white mb-2">
          Reservation Requests
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Keep track of all your guest's reservations on this dashboard.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center justify-between mb-4">
        <ReservationsTab
          onFrameChange={(frame) => setSelectedFrame(frame as FrameType)}
          selectedFrame={selectedFrame}
        />
      </div>

      {/* Refresh + Search + Sort + Favorites */}
      <RequestsToolbar
        searchQuery={searchTermLocal}
        onSearchChange={(value) => {
          setSearchTermLocal(value);
          if (value === "") {
            setSearchTerm("");
          }
        }}
        onSearchSubmit={() => {
          setSearchTerm(searchTermLocal);
          setPage(1); // Reset to first page when searching
        }}
        isLoading={isLoading}
        onRefresh={() => window.location.reload()}
      >
        {/* Sort + Favorites */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative inline-block w-full md:w-56">
            <button
              className="flex dropdown-toggle w-full items-center justify-between 
              px-4 py-2 text-sm text-black bg-white border rounded-md
              hover:bg-gray-100 dark:border-gray-800 dark:text-white/90 dark:bg-white/[0.03]"
              onClick={toggleSortDropdown}
            >
              <span className="px-4">
                {`Sort By ${sortBy === "time" ? "Time" : "Facility Type"}`}
              </span>
              <ChevronDownIcon className="w-4 h-4 text-black dark:text-white/90" />
            </button>
            <Dropdown
              isOpen={isSortDropdownOpen}
              onClose={toggleSortDropdown}
              className="w-full absolute z-100"
            >
              {["Time", "Facility Type"].map((option) => (
                <DropdownItem
                  className="dark:text-white/90"
                  key={option}
                  onItemClick={() => {
                    setSortBy(option === "Time" ? "time" : "requestId");
                    setPage(1);
                    toggleSortDropdown();
                  }}
                >
                  {`Sort By ${option}`}
                </DropdownItem>
              ))}
            </Dropdown>
          </div>
          <button
            className={`flex items-center justify-center px-4 py-2 text-sm border rounded-md
              ${
                showStarredOnly
                  ? "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800"
                  : "bg-white text-black border-gray-200 dark:bg-white/[0.03] dark:text-white/90 dark:border-gray-800"
              }`}
            onClick={() => {
              setShowStarredOnly(!showStarredOnly);
              setPage(1);
            }}
          >
            {showStarredOnly ? (
              <StarIconSolid className="w-5 h-5" />
            ) : (
              <StarIconOutline className="w-5 h-5" />
            )}
          </button>
        </div>
      </RequestsToolbar>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {!fetchedRequests?.data || fetchedRequests.data.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <p className="text-gray-500 dark:text-gray-400">
              No {selectedFrame.toLowerCase()} requests available
            </p>
          </div>
        ) : (
          <Table className="w-full">
            <TableHeader className="border-gray-200 border-b-[3px] dark:border-gray-800">
              <TableRow>
                <TableCell className="font-light dark:text-gray-400 w-12">
                  <span></span>
                </TableCell>
                <TableCell
                  isHeader
                  className="py-3 font-light text-[0.875rem] text-black text-start dark:text-gray-400"
                >
                  Guest Name
                </TableCell>
                <TableCell
                  isHeader
                  className="py-3 font-light text-[0.875rem] text-black text-start dark:text-gray-400"
                >
                  Status
                </TableCell>
                <TableCell
                  isHeader
                  className="py-3 font-light text-[0.875rem] text-black text-start dark:text-gray-400"
                >
                  Facility Name
                </TableCell>
                <TableCell
                  isHeader
                  className="py-3 font-light text-[0.875rem] text-black text-start dark:text-gray-400"
                >
                  Reservation Time
                </TableCell>
                <TableCell
                  isHeader
                  className="py-3 font-light text-[0.875rem] text-black text-start dark:text-gray-400"
                >
                  Party Size
                </TableCell>
                <TableCell
                  isHeader
                  className="py-3 font-light text-[0.875rem] text-black text-start dark:text-gray-400"
                >
                  Duration
                </TableCell>
              </TableRow>
            </TableHeader>
            <TableBody className="divide-y-[1px] divide-gray-200 dark:divide-gray-800">
              {paginatedData.map((request) => (
                <TableRow
                  key={request.requestId}
                  className={`${getRowClassName(
                    request.status,
                  )} cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 ${
                    selectedRequest?.requestId === request.requestId
                      ? "bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500"
                      : ""
                  }`}
                  onClick={() => handleSelectRequest(request)}
                >
                  <TableCell className="py-3 w-12">
                    <button
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        toggleStars(request.requestId);
                      }}
                    >
                      {starredList.has(request.requestId) ? (
                        <StarIconSolid className="w-5 h-5 text-yellow-500" />
                      ) : (
                        <StarIconOutline className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                      )}
                    </button>
                  </TableCell>
                  <TableCell
                    className={`py-3 text-[#1100D8] text-[0.875rem] font-light dark:text-white ${getFrameClass()}`}
                  >
                    {displayValue(request.guestName)}
                  </TableCell>
                  <TableCell className={`py-3 min-w-[120px] ${getFrameClass()}`}>
                    <div className="relative inline-block">
                      <button
                        ref={(el) => {
                          statusButtonRefs.current[request.requestId] = el;
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleStatusDropdown(request.requestId);
                        }}
                        disabled={updatingStatus[request.requestId]}
                        className="flex items-center border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {updatingStatus[request.requestId] ? (
                          <div className="flex items-center gap-2">
                            <LoadingSpinner size="sm" color="primary" />
                            <span>Updating...</span>
                          </div>
                        ) : (
                          <>
                            <span className="mr-2">
                              {getStatusDisplay(request.status) ===
                              "In Progress" ? (
                                <>
                                  <span className="hidden xsm:inline whitespace-nowrap">
                                    In Progress
                                  </span>
                                  <span className="inline xsm:hidden whitespace-nowrap">
                                    In Prog
                                  </span>
                                </>
                              ) : (
                                <span className="whitespace-nowrap">
                                  {getStatusDisplay(request.status)}
                                </span>
                              )}
                            </span>
                            <ChevronDownIcon className="w-4 h-4" />
                          </>
                        )}
                      </button>
                      {statusDropdowns[request.requestId] &&
                        !updatingStatus[request.requestId] && (
                          <Dropdown
                            isOpen={statusDropdowns[request.requestId]}
                            onClose={() =>
                              setStatusDropdowns((prev) => ({
                                ...prev,
                                [request.requestId]: false,
                              }))
                            }
                            className="w-36 z-50"
                            usePortal={true}
                            buttonRef={
                              {
                                current:
                                  statusButtonRefs.current[request.requestId],
                              } as React.RefObject<HTMLElement>
                            }
                          >
                            {["Submitted", "Completed"]
                              .filter(
                                (opt) =>
                                  opt !== getStatusDisplay(request.status),
                              )
                              .map((opt) => (
                                <DropdownItem
                                  key={opt}
                                  onItemClick={() => {
                                    handleTableStatusChange(
                                      request.requestId,
                                      opt,
                                    );
                                  }}
                                  className="dark:text-white"
                                >
                                  {opt}
                                </DropdownItem>
                              ))}
                          </Dropdown>
                        )}
                    </div>
                  </TableCell>
                  <TableCell
                    className={`py-3 text-[#1100D8] text-[0.875rem] font-light dark:text-white ${getFrameClass()}`}
                  >
                    {displayValue(
                      request.facility?.name,
                      displayValue(request.facilityType),
                    )}
                  </TableCell>
                  <TableCell
                    className={`py-3 text-[#1100D8] text-[0.875rem] font-light dark:text-white ${getFrameClass()}`}
                  >
                    <SimpleDate date={request.reservationTime} />
                  </TableCell>
                  <TableCell
                    className={`py-3 text-[#1100D8] text-[0.875rem] font-light dark:text-white ${getFrameClass()}`}
                  >
                    {displayValue(request.partySize)}
                  </TableCell>
                  <TableCell
                    className={`py-3 text-[#1100D8] text-[0.875rem] font-light dark:text-white ${getFrameClass()}`}
                  >
                    {displayValue(request.duration)}{" "}
                    {request.duration ? "min" : ""}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pagination Controls */}
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">Show</span>
          <select
            value={pagination.limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="border rounded px-3 py-1 text-sm bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white pr-8"
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            items per page
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setPage(pagination.page - 1)}
            disabled={pagination.page === 1}
            className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-700 dark:text-white dark:bg-gray-800"
          >
            Previous
          </button>
          <span className="text-sm dark:text-gray-400">
            Page {pagination.page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(pagination.page + 1)}
            disabled={!hasMore}
            className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-700 dark:text-white dark:bg-gray-800"
          >
            Next
          </button>
        </div>
      </div>

      {/* Backdrop: click outside sidebar (including header/app nav) to close all sidebars */}
      {(selectedRequest !== null || showMessageHistory) && (
        <div
          aria-hidden
          className="fixed inset-0 z-40 top-0 left-0 right-0 bottom-0"
          onClick={() => {
            setShowMessageHistory(false);
            setSelectedRequest(null);
          }}
        />
      )}
      {/* SIDEBAR CONTAINER WITH ANIMATION */}
      <div
        className={`
          fixed right-0 top-[70px] w-[30%] h-[calc(100vh-70px)] z-50
          px-5 py-5 shadow-[0_0_15px_rgba(0,0,0,0.1)] dark:shadow-[0_0_15px_rgba(255,255,255,0.1)] 
          bg-white dark:bg-black overflow-y-auto 
          transition-transform duration-300 ease-in-out transform
          ${showMessageHistory ? "hidden" : ""}
          ${selectedRequest !== null ? "translate-x-0" : "translate-x-full"}
        `}
      >
        {/* SIDEBAR CONTENT - Conditionally rendered */}
        {selectedRequest !== null && (
          <>
            {/* Navigation and Close Button */}
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    if (!selectedRequest) return;
                    const currentIndex = sortedData.findIndex(
                      (r) => r.requestId === selectedRequest.requestId,
                    );

                    // If we're at the first item of the page and not on page 1, go to previous page
                    if (currentIndex === 0 && pagination.page > 1) {
                      setPendingNavigation("previous");
                      setPage(pagination.page - 1);
                      return;
                    }

                    // Otherwise, navigate within the current page
                    if (currentIndex > 0) {
                      handleSelectRequest(sortedData[currentIndex - 1]);
                    }
                  }}
                  disabled={
                    !selectedRequest ||
                    (sortedData.findIndex(
                      (r) => r.requestId === selectedRequest.requestId,
                    ) === 0 &&
                      pagination.page === 1)
                  }
                  className="text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full p-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Previous request"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={() => {
                    if (!selectedRequest) return;
                    const currentIndex = sortedData.findIndex(
                      (r) => r.requestId === selectedRequest.requestId,
                    );

                    // If we're at the last item of the page and there's a next page, go to next page
                    if (currentIndex === sortedData.length - 1 && hasMore) {
                      setPendingNavigation("next");
                      setPage(pagination.page + 1);
                      return;
                    }

                    // Otherwise, navigate within the current page
                    if (currentIndex < sortedData.length - 1) {
                      handleSelectRequest(sortedData[currentIndex + 1]);
                    }
                  }}
                  disabled={
                    !selectedRequest ||
                    (sortedData.findIndex(
                      (r) => r.requestId === selectedRequest.requestId,
                    ) ===
                      sortedData.length - 1 &&
                      !hasMore)
                  }
                  className="text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full p-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Next request"
                >
                  <ChevronLeftIcon className="w-5 h-5 rotate-180" />
                </button>
              </div>
              <button
                className="text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full p-2 transition-colors"
                onClick={() => setSelectedRequest(null)}
              >
                <CloseIcon />
              </button>
            </div>

            {/* Star & Timestamp */}
            <div className="flex items-center gap-5 mb-4">
              <button onClick={() => toggleStars(selectedRequest.requestId)}>
                {starredList.has(selectedRequest.requestId) ? (
                  <StarIconSolid className="w-5 h-5 text-yellow-500" />
                ) : (
                  <StarIconOutline className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                )}
              </button>
              <p className="font-light text-gray-500 dark:text-gray-300 italic">
                Submitted:{" "}
                {formatDateWithTimezone(
                  selectedRequest.createdAt,
                  hotelInfo?.timezone,
                )}
              </p>
            </div>

            {/* Status Dropdown */}
            <div className="flex items-center mb-2">
              <p className="font-medium text-black dark:text-white mr-2">
                Status:
              </p>
              <div className="relative">
                <button
                  onClick={() =>
                    setIsSidebarStatusDropdownOpen(!isSidebarStatusDropdownOpen)
                  }
                  className="flex items-center border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-gray-50 dark:bg-gray-700 text-black dark:text-white"
                  disabled={isUpdatingStatus}
                >
                  <span className="mr-1">
                    {getStatusDisplay(selectedRequest.status as RequestStatus)}
                  </span>
                  <ChevronDownIcon className="w-4 h-4" />
                </button>
                {isSidebarStatusDropdownOpen && (
                  <Dropdown
                    isOpen={isSidebarStatusDropdownOpen}
                    onClose={() => setIsSidebarStatusDropdownOpen(false)}
                    className="w-36"
                  >
                    {statusOptions.map((opt) => (
                      <DropdownItem
                        key={opt}
                        onItemClick={() => handleSidebarStatusChange(opt)}
                        className="dark:text-white"
                      >
                        {getStatusDisplay(opt)}
                      </DropdownItem>
                    ))}
                  </Dropdown>
                )}
              </div>
              {isUpdatingStatus && (
                <div className="flex items-center gap-2 ml-2">
                  <LoadingSpinner size="sm" color="primary" />
                  <span className="text-sm text-gray-500">
                    Updating status...
                  </span>
                </div>
              )}
              {statusError && (
                <span className="ml-2 text-sm text-red-500">{statusError}</span>
              )}
            </div>

            {/* Notification Note */}
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Note: The guest will be notified of this status change.
            </p>

            {/* Facility Details and Notes */}
            <div className="mb-4 space-y-3 text-sm">
              <h3 className="text-lg font-semibold text-black dark:text-white mb-2 border-b pb-1 dark:border-gray-700">
                Facility Details
              </h3>
              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Facility:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {displayValue(
                    selectedRequest.facility?.name,
                    displayValue(selectedRequest.facilityType),
                  )}
                </p>
              </div>
              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Facility Type:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {displayValue(selectedRequest.facility?.type)}
                </p>
              </div>

              {/* Reservation Details */}
              <h3 className="text-lg font-semibold text-black dark:text-white mb-2 border-b pb-1 dark:border-gray-700">
                Reservation Details
              </h3>
              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Guest Name:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {displayValue(
                    selectedRequest.guestInfo?.name ||
                      selectedRequest.guestName,
                  )}
                </p>
              </div>
              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Party Size:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {displayValue(selectedRequest.partySize)}
                </p>
              </div>

              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Reserved From:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {formatDateWithTimezone(
                    selectedRequest.reservationTime,
                    hotelInfo?.timezone,
                  )}
                </p>
              </div>
              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Reserved To:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {(() => {
                    const startTime = new Date(selectedRequest.reservationTime);
                    const endTime = new Date(
                      startTime.getTime() +
                        (selectedRequest.duration || 0) * 60000,
                    );
                    return formatDateWithTimezone(
                      endTime.toISOString(),
                      hotelInfo?.timezone,
                    );
                  })()}
                </p>
              </div>
              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Duration:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {displayValue(selectedRequest.duration)}{" "}
                  {selectedRequest.duration ? "minutes" : ""}
                </p>
              </div>
              {selectedRequest.completedAt && (
                <div className="flex items-start">
                  <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                    Completed:
                  </p>
                  <p className="font-light text-black dark:text-white break-words">
                    {formatDateWithTimezone(
                      selectedRequest.completedAt,
                      hotelInfo?.timezone,
                    )}
                  </p>
                </div>
              )}

              {/* Special Requests and Notes */}
              {selectedRequest.specialRequests && (
                <>
                  <div className="pt-2 mt-2 border-t dark:border-gray-700"></div>
                  <div>
                    <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Special Requests:
                    </p>
                    <p className="font-light text-black dark:text-white italic">
                      "
                      {displayValue(
                        selectedRequest.specialRequests,
                        "No special requests.",
                      )}
                      "
                    </p>
                  </div>
                </>
              )}

              {selectedRequest.notes && (
                <>
                  <div>
                    <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Notes:
                    </p>
                    <p className="font-light text-black dark:text-white italic">
                      "
                      {displayValue(
                        selectedRequest.notes,
                        "No notes provided.",
                      )}
                      "
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Live Status Field */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-black dark:text-white mb-4">
                Send a Message
              </h3>
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg">
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-0 focus:border-gray-300 dark:focus:border-gray-600 border-b border-gray-300 dark:border-gray-600 rounded-t-lg border-x-0 border-t-0"
                  placeholder="Enter subject (optional)..."
                />
                {/* Message Type Dropdown */}
                <div className="px-3 py-2 border-b border-gray-300 dark:border-gray-600">
                  <label
                    htmlFor="messageTypeReservation"
                    className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1"
                  >
                    Message Type
                  </label>
                  {isLoadingMessageTypes ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Loading types...
                    </p>
                  ) : messageTypeError ? (
                    <p className="text-sm text-red-500">
                      Error: {messageTypeError.message}
                    </p>
                  ) : (
                    <select
                      id="messageTypeReservation"
                      value={selectedMessageTypeId}
                      onChange={(e) => setSelectedMessageTypeId(e.target.value)}
                      className="w-full bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none text-sm rounded-md p-1 border border-gray-300 dark:border-gray-600"
                      disabled={messageTypes.length === 0}
                    >
                      {messageTypes.length === 0 && (
                        <option key="no-types" value="">
                          No types available
                        </option>
                      )}
                      {messageTypes.map((type) => (
                        <option key={type.typeid} value={type.typeid}>
                          {type.type}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <textarea
                  value={liveStatus}
                  onChange={(e) => setLiveStatus(e.target.value)}
                  rows={3}
                  className="w-full px-3 pt-2 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-0 border-0 rounded-b-lg"
                  placeholder="Enter message content (required)..."
                  disabled={isPushingStatus}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <button
                  onClick={() => setShowMessageHistory(true)}
                  className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600"
                >
                  View Message History
                </button>
                <button
                  onClick={handlePushToGuest}
                  disabled={isPushingStatus}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-[#1100D8] rounded-md hover:bg-[#0e00b8] dark:bg-white/10 dark:hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isPushingStatus ? (
                    <div className="flex items-center gap-2">
                      <LoadingSpinner size="sm" color="primary" />
                      <span>Sending...</span>
                    </div>
                  ) : (
                    "Send to Guest"
                  )}
                </button>
              </div>
              {showPushAlert && (
                <div className="mt-3">
                  <Alert
                    variant="success"
                    title=""
                    message="Message successfully sent to guest!"
                  />
                </div>
              )}
            </div>

            {/* Additional Sidebar Fields (Reformatted Structure) */}
            <div className="space-y-3 text-sm">
              <h3 className="text-lg font-semibold text-black dark:text-white mb-2 border-b pb-1 dark:border-gray-700">
                Request Details
              </h3>
              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Request ID:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {displayValue(selectedRequest.requestId)}
                </p>
              </div>
              {/* Guest Information */}
              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Guest Email:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {displayValue(
                    selectedRequest.guestInfo?.email ||
                      selectedRequest.guestContact?.email,
                  )}
                </p>
              </div>
              <div className="flex items-start">
                <p className="w-28 font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                  Guest Phone:
                </p>
                <p className="font-light text-black dark:text-white break-words">
                  {displayValue(
                    selectedRequest.guestInfo?.phone ||
                      selectedRequest.guestContact?.phone,
                  )}
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmDialog}
        onConfirm={handleConfirmSend}
        onCancel={handleCancelSend}
        title="Confirm Message"
        message={`Are you sure you want to send this message to the guest?${
          pendingMessage?.subject
            ? `\n\nSubject: ${pendingMessage.subject}`
            : ""
        }\n\nContent: ${pendingMessage?.content || ""}`}
        isLoading={isPushingStatus}
      />

      {/* Message History Sidebar */}
      <MessageHistorySidebar
        isOpen={showMessageHistory}
        onClose={() => {
          setShowMessageHistory(false);
          try {
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.delete("requestId");
              return next;
            });
          } catch {}
        }}
        requestId={selectedRequest?.requestId}
      />

      {/* Status Update Success Toast */}
      {showStatusUpdateAlert && (
        <div className="fixed bottom-4 right-4 z-50 animate-fade-in">
          <Alert
            variant="success"
            title="Status Updated"
            message="Request status has been updated successfully."
          />
        </div>
      )}
    </div>
  );
}
